import {
  Business as CompanyIcon,
  Description as DocumentsIcon,
  LocalShipping as VehicleIcon,
  Person as DriverIcon,
  RvHookup as TrailerIcon,
  CloudUpload as UploadIcon,
} from "@mui/icons-material";
import { Autocomplete, Box, Button, TextField, Typography } from "@mui/material";
import React, { useMemo, useState } from "react";
import PageTitle from "../../components/common/PageTitle";
import TabbedPageCard from "../../components/common/TabbedPageCard";
import PageLayout from "../../components/layout/PageLayout";
import { useGetTruckDriversQuery } from "../../api/employeesApi";
import { useGetTrailersQuery } from "../../api/trailersApi";
import { useGetVehiclesQuery } from "../../api/vehiclesApi";
import DocumentsTable from "./components/DocumentsTable";
import DocumentUploadModal, {
  DocumentEntityType,
} from "../../components/documents/DocumentUploadModal";
import VehicleDocumentsTab from "../vehicles/components/VehicleDocumentsTab";
import TrailerDocumentsTab from "../trailers/components/TrailerDocumentsTab";

const DocumentsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedVehicleId, setSelectedVehicleId] = useState<number | null>(null);
  const [selectedTrailerId, setSelectedTrailerId] = useState<number | null>(null);
  const { data: vehicles = [] } = useGetVehiclesQuery();
  const { data: trailers = [] } = useGetTrailersQuery();
  const { data: drivers = [] } = useGetTruckDriversQuery();

  const vehicleOptions = useMemo(
    () =>
      vehicles.map((vehicle) => ({
        id: vehicle.id,
        label: vehicle.name || vehicle.vin || `Vehicle #${vehicle.id}`,
      })),
    [vehicles]
  );

  const selectedVehicleOption = useMemo(
    () => vehicleOptions.find((option) => option.id === selectedVehicleId) ?? null,
    [selectedVehicleId, vehicleOptions]
  );

  const trailerOptions = useMemo(
    () =>
      trailers
        .filter((trailer) => !trailer.isArchived)
        .map((trailer) => ({
          id: trailer.id,
          label: trailer.unitNumber || trailer.vin || `Trailer #${trailer.id}`,
        })),
    [trailers]
  );

  const selectedTrailerOption = useMemo(
    () => trailerOptions.find((option) => option.id === selectedTrailerId) ?? null,
    [selectedTrailerId, trailerOptions]
  );

  const driverOptions = useMemo(
    () =>
      drivers.map((driver) => ({
        id: driver.id,
        label: `${driver.firstName} ${driver.lastName}`.trim() || driver.email,
      })),
    [drivers]
  );

  const uploadContext = useMemo<{
    entityType: DocumentEntityType;
    entityLabel: string;
    entityOptions?: { id: number; label: string }[];
  }>(() => {
    switch (activeTab) {
      case 1:
        return {
          entityType: "Vehicle",
          entityLabel: "Vehicle",
          entityOptions: vehicleOptions,
        };
      case 2:
        return {
          entityType: "Trailer",
          entityLabel: "Trailer",
          entityOptions: trailerOptions,
        };
      case 3:
        return {
          entityType: "employee",
          entityLabel: "Driver",
          entityOptions: driverOptions,
        };
      case 0:
      default:
        return {
          entityType: "Company",
          entityLabel: "Company",
        };
    }
  }, [activeTab, driverOptions, trailerOptions, vehicleOptions]);

  const renderEntityPickerPlaceholder = (entityLabel: string) => (
    <Box sx={{ p: 3, textAlign: "center" }}>
      <Typography variant="h6" color="text.secondary" gutterBottom>
        Select a {entityLabel.toLowerCase()}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Choose a {entityLabel.toLowerCase()} above to view and reorder its documents.
      </Typography>
    </Box>
  );

  const renderVehicleDocumentsTab = () => (
    <Box>
      <Autocomplete
        size="small"
        options={vehicleOptions}
        value={selectedVehicleOption}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_event, option) => setSelectedVehicleId(option?.id ?? null)}
        renderInput={(params) => (
          <TextField {...params} label="Select Vehicle" />
        )}
        sx={{ maxWidth: 420, mb: 2 }}
      />

      {selectedVehicleId ? (
        <VehicleDocumentsTab
          vehicleId={selectedVehicleId}
          showHeader={false}
          showCompanyTab={false}
        />
      ) : (
        renderEntityPickerPlaceholder("Vehicle")
      )}
    </Box>
  );

  const renderTrailerDocumentsTab = () => (
    <Box>
      <Autocomplete
        size="small"
        options={trailerOptions}
        value={selectedTrailerOption}
        getOptionLabel={(option) => option.label}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_event, option) => setSelectedTrailerId(option?.id ?? null)}
        renderInput={(params) => (
          <TextField {...params} label="Select Trailer" />
        )}
        sx={{ maxWidth: 420, mb: 2 }}
      />

      {selectedTrailerId ? (
        <TrailerDocumentsTab
          trailerId={selectedTrailerId}
          showHeader={false}
          showCompanyTab={false}
        />
      ) : (
        renderEntityPickerPlaceholder("Trailer")
      )}
    </Box>
  );

  return (
    <PageLayout>
      <PageTitle
        title="Documents"
        icon={<DocumentsIcon />}
        titleVariant="h4"
        subtitle="Manage company, vehicle, trailer, and driver documents from one place"
        actions={
          <Button
            variant="contained"
            startIcon={<UploadIcon />}
            onClick={() => setUploadModalOpen(true)}
            sx={{ textTransform: "none" }}
          >
            Upload Document
          </Button>
        }
      />

      <TabbedPageCard
        idPrefix="documents"
        ariaLabel="document entity tabs"
        selectedTab={activeTab}
        onTabChange={setActiveTab}
        panelSx={{ p: 2 }}
        tabs={[
          {
            label: "Company",
            icon: <CompanyIcon />,
            iconPosition: "start",
            content: (
              <DocumentsTable
                entityType="Company"
                entityLabel="Company"
                showEntityFilter={false}
              />
            ),
          },
          {
            label: "Vehicles",
            icon: <VehicleIcon />,
            iconPosition: "start",
            content: renderVehicleDocumentsTab(),
          },
          {
            label: "Trailers",
            icon: <TrailerIcon />,
            iconPosition: "start",
            content: renderTrailerDocumentsTab(),
          },
          {
            label: "Drivers",
            icon: <DriverIcon />,
            iconPosition: "start",
            content: (
              <DocumentsTable
                entityType="employee"
                entityLabel="Driver"
                entityOptions={driverOptions}
              />
            ),
          },
        ]}
      />

      <DocumentUploadModal
        open={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        entityType={uploadContext.entityType}
        entityId={uploadContext.entityType === "Company" ? 0 : undefined}
        entityLabel={uploadContext.entityLabel}
        entityOptions={uploadContext.entityOptions}
        onSuccess={() => setUploadModalOpen(false)}
      />
    </PageLayout>
  );
};

export default DocumentsPage;
